import type { ComponentType } from "react";

import * as ReactWidgets from "@fluid-app/react-widgets";

import type { PropInfo } from "./utils/createWebComponent";
import bannerWidgetProps, {
  propInfo as bannerPropInfo,
} from "../generated/banner-widget-props.js";
// Statically import all prop files to bundle them directly
import cartWidgetProps, {
  propInfo as cartPropInfo,
} from "../generated/cart-widget-props.js";
import leadCaptureWidgetProps, {
  propInfo as leadCapturePropInfo,
} from "../generated/lead-capture-widget-props.js";
// Import the generated manifest
import widgetManifest from "../generated/widget-manifest.js";
import { createWebComponent } from "./utils/createWebComponent";

// Define the structure of the manifest entries
interface WidgetInfo {
  dirName: string;
  componentName: string;
  tagName: string;
  propsJsPath: string;
}

// Create a static registry of prop information
const STATIC_PROP_REGISTRY = {
  "cart-widget": {
    propNames: cartWidgetProps,
    propInfo: cartPropInfo as PropInfo[],
  },
  "banner-widget": {
    propNames: bannerWidgetProps,
    propInfo: bannerPropInfo as PropInfo[],
  },
  "lead-capture-widget": {
    propNames: leadCaptureWidgetProps,
    propInfo: leadCapturePropInfo as PropInfo[],
  },
} as const;

// Define a custom window interface with Fluid SDK properties
interface FluidWindow extends Window {
  fluidCart?: {
    open: () => void;
    close: () => void;
    toggle: () => void;
  };
}
// Type-safe access to the window object
const fluidWindow = window as FluidWindow;

/**
 * Register all web components based on the generated manifest
 */
export async function registerWebComponents(): Promise<void> {
  if (typeof window === "undefined" || typeof customElements === "undefined") {
    console.warn(
      "Web components registration failed: not running in a browser environment",
    );
    return;
  }

  try {
    for (const widget of widgetManifest as WidgetInfo[]) {
      // Check if component is already registered - be more explicit about the check
      const existingElement = customElements.get(widget.tagName);
      if (existingElement) {
        console.log(
          `Custom element "${widget.tagName}" is already registered. Skipping.`,
        );
        continue;
      }

      // Dynamically access the potential React component from the imported module
      // Cast ReactWidgets to an indexable type to allow string access
      const PotentialComponent = (ReactWidgets as Record<string, unknown>)[
        widget.componentName
      ];

      // Check if the accessed property is a function (React components are functions/classes)
      if (typeof PotentialComponent !== "function") {
        console.warn(
          `Could not find a valid React component "${widget.componentName}" in @fluid-app/react-widgets exports, or it was not a function/class.`,
        );
        continue; // Skip to the next widget
      }

      // Now we know PotentialComponent is a function, treat it as the component
      const Component = PotentialComponent as ComponentType<unknown>;

      // Load prop information
      const { propNames, propInfo } = await loadPropInfo(widget);

      // Create and define the web component
      // Use detailed prop info if available, otherwise fall back to prop names
      const propsForComponent = propInfo.length > 0 ? propInfo : propNames;
      const WebComponentElement = createWebComponent(
        Component,
        propsForComponent,
        widget.tagName,
      );

      // Final check before defining - double-check that the element isn't defined
      // This handles race conditions where another script might have defined it
      // between our initial check and component creation
      if (customElements.get(widget.tagName)) {
        console.log(
          `Custom element "${widget.tagName}" was defined during component creation. Skipping definition.`,
        );
        continue;
      }

      try {
        // Try to define the custom element with comprehensive error handling
        customElements.define(widget.tagName, WebComponentElement);
        console.log(
          `✅ Successfully registered custom element "${widget.tagName}"`,
        );
      } catch (defineError) {
        // Handle the specific error we're trying to prevent
        if (
          defineError instanceof Error &&
          (defineError.message.includes("already been defined") ||
            defineError.message.includes(
              "already been used with this registry",
            ) ||
            defineError.message.includes("has already been used"))
        ) {
          // This is the exact error from the issue - log it but don't fail
          console.log(
            `Custom element "${widget.tagName}" already defined elsewhere. Skipping registration.`,
          );
        } else {
          // For other errors, log warning but don't fail the entire registration process
          console.error(
            `Error defining custom element "${widget.tagName}":`,
            defineError,
          );
        }
      }
    }
  } catch (error) {
    console.error(
      "Failed to register Fluid web components dynamically:",
      error instanceof Error ? error.message : String(error),
    );
  }
}

/**
 * Load prop information for a widget
 */
async function loadPropInfo(
  widget: WidgetInfo,
): Promise<{ propNames: string[]; propInfo: PropInfo[] }> {
  // First try to get props from static registry (bundled)
  if (widget.dirName in STATIC_PROP_REGISTRY) {
    const staticProps =
      STATIC_PROP_REGISTRY[widget.dirName as keyof typeof STATIC_PROP_REGISTRY];
    console.log(`✅ Loaded props for ${widget.tagName} from static registry`);
    return {
      propNames: staticProps.propNames,
      propInfo: staticProps.propInfo,
    };
  }

  // Fallback: try loading from /generated folder
  try {
    const propFileName = `${widget.dirName}-props.js`;
    const propsModule = (await import(
      /* webpackIgnore: true */ /* @vite-ignore */ `/generated/${propFileName}`
    )) as {
      default: string[];
      propInfo?: PropInfo[];
    };

    if (Array.isArray(propsModule.default)) {
      console.log(
        `✅ Loaded props for ${widget.tagName} from /generated/${propFileName}`,
      );
      return {
        propNames: propsModule.default,
        propInfo: propsModule.propInfo ?? [],
      };
    }
  } catch (error) {
    console.debug(`Failed to load props from /generated folder:`, error);
  }

  // If all else fails, return empty props
  console.warn(
    `⚠️  Could not load props for ${widget.tagName}. Widget will work but without prop validation.`,
  );
  return { propNames: [], propInfo: [] };
}

// Auto-register web components if in browser environment
if (typeof window !== "undefined") {
  registerWebComponents()
    .then(() => {
      if (customElements.get("fluid-cart-widget")) {
        // Destructure directly, relying on subsequent type checks
        const { openCart, closeCart, toggleCart } = ReactWidgets;

        // Ensure the specific functions exist and are functions before assigning
        if (
          typeof openCart === "function" &&
          typeof closeCart === "function" &&
          typeof toggleCart === "function"
        ) {
          // Assign with explicit casting after check
          fluidWindow.fluidCart = {
            open: openCart as () => void,
            close: closeCart as () => void,
            toggle: toggleCart as () => void,
          };
        } else {
          console.warn(
            "Cart control functions (openCart, closeCart, toggleCart) not found or not functions in react-widgets exports.",
          );
        }

        // Add click listener for data-fluid-cart attribute
        document.addEventListener("click", (e) => {
          // Use optional chaining for potentially undefined fluidCart or methods
          const cartApi = fluidWindow.fluidCart;
          if (!cartApi) return;

          const target = e.target as HTMLElement;
          const button = target.closest("[data-fluid-cart]");
          if (!button) return;

          const action = button.getAttribute("data-fluid-cart");

          if (action === "open" && typeof cartApi.open === "function")
            cartApi.open();
          else if (action === "close" && typeof cartApi.close === "function")
            cartApi.close();
          else if (action === "toggle" && typeof cartApi.toggle === "function")
            cartApi.toggle();
        });
      }
    })
    .catch((err) => {
      console.error("Error during async web component registration:", err);
    });
}

// Export types for documentation (This part becomes less useful as components are dynamic)
// Consider generating a separate types file or improving documentation.
// export type { BannerWidgetProps, CartWidgetProps }; // Commenting out for now

// export type { BannerWidgetProps, CartWidgetProps }; // Commenting out for now
